import React, { useState } from 'react';
import { CAPABILITIES, GRID_COLUMNS, GROUPS, normalizeGrants } from '../../utils/staffCapabilities';
import { Btn, GridCheck, MAGENTA, useAdminShell } from './shell';

const HEADS = ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXTRA'];

/**
 * The capability grid. Rows in registry order under magenta group labels, columns
 * VIEW · CREATE · EDIT · DELETE · EXTRA. A cell is a 28px radius-9 box: on is black
 * with a white check, off is #F2F2F2; a verb a section does not have draws an em-dash,
 * never a box, and the EXTRA cell names its verb. Each row carries a 30px mark circle
 * (black once anything is granted) and `n OF m`; clicking the section NAME grants or
 * clears the whole row.
 *
 * The STAFF row is the only unusable control drawn anywhere in the panel — superadmin
 * is granted in the database and nothing here can change that. At phone width the grid
 * becomes two screens: the sections with `n OF m`, then one section's verbs.
 */
export default function CapabilityGrid({ grants, onChange, readOnly = false }) {
  const { isMobile } = useAdminShell();
  const [openKey, setOpenKey] = useState(null);
  const g = normalizeGrants(grants);
  const has = (key, verb) => !!g[key]?.includes(verb);
  const count = (c) => (g[c.key] || []).length;

  const toggle = (key, verb) => {
    if (readOnly) return;
    const next = { ...g, [key]: has(key, verb) ? (g[key] || []).filter((v) => v !== verb) : [...(g[key] || []), verb] };
    if (next[key].length === 0) delete next[key];
    onChange(next);
  };

  /** Clicking the section name is "all of it" or "none of it". */
  const toggleRow = (c) => {
    if (readOnly || c.superadminOnly) return;
    const all = [...c.verbs];
    const next = { ...g };
    if (count(c) === all.length) delete next[c.key];
    else next[c.key] = all;
    onChange(next);
  };

  const markFor = (c) => (
    <span
      className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full font-mono text-[8px] font-extrabold"
      style={count(c) > 0 ? { background: '#000', color: '#fff' } : { background: '#F2F2F2', color: '#4B5563' }}
    >
      {c.mark}
    </span>
  );

  if (isMobile) {
    if (openKey) {
      const c = CAPABILITIES.find((x) => x.key === openKey);
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <Btn size="card" onClick={() => setOpenKey(null)}>← ALL SECTIONS</Btn>
            <span className="font-mono text-[12px] font-bold uppercase tracking-[0.12em]">{c.label}</span>
          </div>
          {c.verbs.map((v) => {
            const on = has(c.key, v);
            return (
              <button
                key={v}
                type="button"
                role="checkbox"
                aria-checked={on}
                aria-label={`${c.label} ${v}`}
                onClick={() => toggle(c.key, v)}
                className="flex h-[52px] w-full items-center justify-between gap-3 rounded-full bg-[#F7F7F7] px-5 text-left"
              >
                <span className="font-mono text-[12px] font-bold uppercase tracking-[0.12em]">{v}</span>
                <span className={`flex h-7 w-7 items-center justify-center rounded-[9px] ${on ? 'bg-black text-white' : 'bg-white text-transparent'}`}><GridCheck size={16} /></span>
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        {CAPABILITIES.map((c) => {
          const locked = c.superadminOnly;
          return (
            <button
              key={c.key}
              type="button"
              disabled={locked}
              onClick={() => setOpenKey(c.key)}
              className={`flex h-[52px] w-full items-center gap-3 rounded-full bg-[#F7F7F7] py-0 pl-[6px] pr-5 text-left ${locked ? 'opacity-60' : ''}`}
            >
              {markFor(c)}
              <span className="min-w-0 flex-1 truncate font-mono text-[12px] font-bold uppercase tracking-[0.1em]">{c.label}</span>
              <span className={`whitespace-nowrap font-mono text-[10px] font-bold tracking-[0.12em] ${locked || count(c) === 0 ? 'text-[#9CA3AF]' : 'text-black'}`}>
                {locked ? 'SUPERADMIN ONLY' : `${count(c)} OF ${c.verbs.length}`}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  const cols = '260px repeat(5, 1fr) 90px';
  /** The STAFF row: one pair of locked pills across every verb column. */
  const lockedCells = (
    <div className="flex items-center justify-center gap-2 px-2 py-2" style={{ gridColumn: 'span 5' }}>
      {['SUPERADMIN ONLY', 'GRANTED IN THE DATABASE'].map((t) => (
        <span key={t} className="flex h-7 items-center whitespace-nowrap rounded-full bg-[#F2F2F2] px-[10px] font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-[#9CA3AF]">{t}</span>
      ))}
    </div>
  );

  const cell = (c, verb, extra = false) => {
    if (!verb || !c.verbs.includes(verb)) return <div className="flex items-center justify-center px-2 py-2 font-mono text-[11px] font-bold text-[#D1D5DB]">—</div>;
    const on = has(c.key, verb);
    return (
      <div className="flex items-center justify-center gap-2 px-2 py-2">
        <button
          type="button"
          role="checkbox"
          aria-checked={on}
          aria-label={`${c.label} ${verb}`}
          disabled={readOnly}
          onClick={() => toggle(c.key, verb)}
          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[9px] transition-colors duration-200 ${on ? 'bg-black text-white' : 'bg-[#F2F2F2] text-transparent'} ${readOnly ? 'cursor-default' : ''}`}
        >
          <GridCheck size={14} />
        </button>
        {extra && <span className="whitespace-nowrap font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">{verb}</span>}
      </div>
    );
  };

  let lastGroup = null;
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[860px]">
        <div className="grid px-2" style={{ gridTemplateColumns: cols }}>
          <div className="px-3 py-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">SECTION</div>
          {HEADS.map((h) => <div key={h} className="px-2 py-2 text-center font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">{h}</div>)}
          <div className="px-2 py-2 text-right font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">GRANTED</div>
        </div>
        {CAPABILITIES.map((c) => {
          const head = c.group !== lastGroup ? GROUPS.find((x) => x.key === c.group) : null;
          lastGroup = c.group;
          return (
            <React.Fragment key={c.key}>
              {head && (
                <div className="px-5 pb-1 pt-4 font-mono text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: MAGENTA }}>{head.label}</div>
              )}
              <div className="grid items-center rounded-[18px] px-2 transition-colors duration-200 hover:bg-[#F7F7F7]" style={{ gridTemplateColumns: cols }}>
                <button
                  type="button"
                  onClick={() => toggleRow(c)}
                  disabled={readOnly || c.superadminOnly}
                  title={c.superadminOnly ? 'Superadmin only' : 'Grant or clear the whole row'}
                  className={`flex min-w-0 items-center gap-3 px-1 py-2 text-left font-mono text-[11px] font-bold uppercase tracking-[0.08em] ${c.superadminOnly ? 'text-[#9CA3AF]' : ''}`}
                >
                  {markFor(c)}
                  <span className="min-w-0 truncate">{c.label}</span>
                </button>
                {c.superadminOnly ? lockedCells : (
                  <>
                    {GRID_COLUMNS.map((v) => <React.Fragment key={v}>{cell(c, v)}</React.Fragment>)}
                    {cell(c, c.extra, true)}
                  </>
                )}
                <div className={`px-2 py-2 text-right font-mono text-[10px] font-bold tracking-[0.1em] ${count(c) === 0 ? 'text-[#9CA3AF]' : 'text-black'}`}>
                  {c.superadminOnly ? '—' : `${count(c)} OF ${c.verbs.length}`}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
